/**
 * Tracking helper do admindesk.
 *
 * Padrao:
 * - Todo push automaticamente injeta page_path, page_location e app_section: "checkout".
 * - NUNCA mandar email/telefone no dataLayer (apenas user_id numerico).
 *   PII vai apenas pelo backend (Measurement Protocol -> Meta CAPI hashado).
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const APP_SECTION = 'checkout';

function push(data: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    page_path: window.location.pathname,
    page_location: window.location.href,
    app_section: APP_SECTION,
    ...data,
  });
}

// ---- Visualizacao da pagina /contratar -------------------------------------

export interface ContratarViewedParams {
  planSlug: string;
  billingCycle: 'mensal' | 'anual';
  isTrial: boolean;
  trialDays?: number;
  hasCoupon: boolean;
  isAuthenticated: boolean;
  userId?: number;
}

export function trackContratarViewed(params: ContratarViewedParams) {
  push({
    event: 'contratar_viewed',
    plan_slug: params.planSlug,
    billing_cycle_selected: params.billingCycle === 'anual' ? 'annual' : 'monthly',
    is_trial: params.isTrial,
    trial_days: params.trialDays ?? 0,
    has_coupon: params.hasCoupon,
    is_authenticated: params.isAuthenticated,
    user_id: params.userId,
  });
}

/**
 * Reaproveita o evento `signup_started` do briefing para que a contagem
 * de "inicios de signup" seja consistente independentemente da entrada.
 */
export function trackSignupStarted(params: {
  planSlug: string;
  isTrial: boolean;
  isAuthenticated: boolean;
  userId?: number;
}) {
  push({
    event: 'signup_started',
    entry_point: 'contratar',
    plan_slug: params.planSlug,
    is_trial: params.isTrial,
    is_authenticated: params.isAuthenticated,
    user_id: params.userId,
  });
}

// ---- Etapas de identificacao do guest --------------------------------------

export function trackCheckoutEmailEntered(params: {
  planSlug: string;
  emailHasAccount: boolean;
}) {
  push({
    event: 'checkout_email_entered',
    plan_slug: params.planSlug,
    email_has_account: params.emailHasAccount,
  });
}

export function trackCheckoutOtpRequested(params: { planSlug: string }) {
  push({
    event: 'checkout_otp_requested',
    plan_slug: params.planSlug,
  });
}

export function trackCheckoutOtpValidated(params: { planSlug: string }) {
  push({
    event: 'checkout_otp_validated',
    plan_slug: params.planSlug,
  });
}

export function trackCheckoutOtpFailed(params: { planSlug: string }) {
  push({
    event: 'checkout_otp_failed',
    plan_slug: params.planSlug,
  });
}

// ---- Selecao de plano e cupom ----------------------------------------------

export function trackCheckoutPlanChanged(params: {
  planSlugFrom?: string;
  planSlugTo: string;
}) {
  push({
    event: 'checkout_plan_changed',
    plan_slug_from: params.planSlugFrom,
    plan_slug_to: params.planSlugTo,
  });
}

export function trackCheckoutBillingCycleChanged(params: {
  planSlug: string;
  billingCycle: 'mensal' | 'anual';
}) {
  push({
    event: 'checkout_billing_cycle_changed',
    plan_slug: params.planSlug,
    billing_cycle_selected: params.billingCycle === 'anual' ? 'annual' : 'monthly',
  });
}

export function trackCheckoutCouponApplied(params: {
  planSlug: string;
  couponCode: string;
  discountValue?: number;
  currency?: string;
}) {
  push({
    event: 'checkout_coupon_applied',
    plan_slug: params.planSlug,
    coupon_code: params.couponCode,
    discount_value: params.discountValue ?? 0,
    currency: params.currency ?? 'BRL',
  });
}

export function trackCheckoutCouponInvalid(params: {
  planSlug: string;
  couponCode: string;
  reason?: string;
}) {
  push({
    event: 'checkout_coupon_invalid',
    plan_slug: params.planSlug,
    coupon_code: params.couponCode,
    reason: params.reason,
  });
}

export function trackCheckoutOrderBumpToggled(params: {
  planSlug: string;
  bumpPriceId: string;
  selected: boolean;
}) {
  push({
    event: 'checkout_order_bump_toggled',
    plan_slug: params.planSlug,
    bump_price_id: params.bumpPriceId,
    selected: params.selected,
  });
}

// ---- Tela de revisao / pagamento -------------------------------------------

export interface CheckoutReviewParams {
  planSlug: string;
  billingCycle: 'mensal' | 'anual';
  subtotal: number;
  discount: number;
  total: number;
  currency: string;
  isTrial: boolean;
  trialDays?: number;
}

export function trackCheckoutReviewViewed(params: CheckoutReviewParams) {
  push({
    event: 'checkout_review_viewed',
    plan_slug: params.planSlug,
    billing_cycle_selected: params.billingCycle === 'anual' ? 'annual' : 'monthly',
    subtotal: params.subtotal,
    discount: params.discount,
    value: params.total,
    currency: params.currency,
    is_trial: params.isTrial,
    trial_days: params.trialDays ?? 0,
  });
}

/**
 * Disparado no momento exato antes do redirect para o Stripe Checkout.
 * E o evento mais importante do funil pre-pagamento.
 */
export function trackPaymentInitiated(params: CheckoutReviewParams & {
  userId?: number;
  gaClientId?: string;
}) {
  push({
    event: 'payment_initiated',
    plan_slug: params.planSlug,
    billing_cycle_selected: params.billingCycle === 'anual' ? 'annual' : 'monthly',
    value: params.total,
    currency: params.currency,
    is_trial: params.isTrial,
    trial_days: params.trialDays ?? 0,
    user_id: params.userId,
    ga_client_id: params.gaClientId,
  });
}

// ---- Pagina de sucesso -----------------------------------------------------

export function trackCheckoutSuccessPolling(params: { sessionId: string }) {
  push({
    event: 'checkout_success_polling',
    transaction_id: params.sessionId,
  });
}

export function trackCheckoutSuccessConfirmed(params: { sessionId: string }) {
  push({
    event: 'checkout_success_confirmed',
    transaction_id: params.sessionId,
  });
}

export function trackCheckoutSuccessFailed(params: {
  sessionId?: string;
  reason: string;
}) {
  push({
    event: 'checkout_success_failed',
    transaction_id: params.sessionId,
    reason: params.reason,
  });
}
